/**
 * API クライアント（fetch ラッパ）
 *
 * - `/api` 配下を呼ぶ。パスは `/admin/users` のように `/api` を除いて指定する。
 * - Cookie セッション（credentials: 'include'）。
 * - CSRF 対策として全リクエストに `X-Requested-With: XMLHttpRequest` を付与。
 * - エラー応答 `{ error: { code, message, details } }` は `ApiError` として throw。
 * - 401 は `setUnauthorizedHandler` で登録したハンドラ（ログイン画面への遷移）を呼ぶ。
 */

export const API_BASE = '/api';

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * フィールド別エラーメッセージ `{ 'items.0.amount': '必須項目です' }`。
   * API の標準形式 `details: [{ path, message }]` と、zod flatten() 形式 `{ fieldErrors }` の両方に対応。
   */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as Array<{ path?: unknown; message?: unknown }>) {
        if (d && typeof d.path === 'string' && typeof d.message === 'string' && !(d.path in out)) out[d.path] = d.message;
      }
      return out;
    }
    const d = this.details as { fieldErrors?: Record<string, string[] | undefined> } | undefined;
    if (d && typeof d === 'object' && d.fieldErrors) {
      for (const [k, v] of Object.entries(d.fieldErrors)) {
        if (v && v[0]) out[k] = v[0];
      }
    }
    return out;
  }
}

/** HTTP ステータスごとの既定メッセージ（サーバーがメッセージを返さない場合） */
const DEFAULT_MESSAGES: Record<number, string> = {
  400: '入力内容に誤りがあります。',
  401: 'ログインの有効期限が切れました。再度ログインしてください。',
  403: 'この操作を行う権限がありません。',
  404: '対象のデータが見つかりません。',
  409: '他のユーザーによって更新されています。画面を再読み込みしてください。',
  429: 'アクセスが集中しています。しばらく待ってから再度お試しください。',
  500: 'サーバーでエラーが発生しました。時間をおいて再度お試しください。',
};

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** 例外から画面表示用メッセージを得る */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return '予期しないエラーが発生しました。';
}

/**
 * 接続元・端末による利用不可（403）。受信したら画面全体を「利用できません」案内に切り替える。
 *   IP_NOT_ALLOWED … IP アドレス制限
 *   DEVICE_CERT_* … 端末証明書（未提示・未登録・失効・期限切れ）
 */
export const ACCESS_BLOCK_CODES: ReadonlySet<string> = new Set([
  'IP_NOT_ALLOWED',
  'DEVICE_CERT_REQUIRED',
  'DEVICE_CERT_NOT_REGISTERED',
  'DEVICE_CERT_REVOKED',
  'DEVICE_CERT_EXPIRED',
]);

export function isAccessBlockedError(e: unknown): e is ApiError {
  return e instanceof ApiError && e.status === 403 && ACCESS_BLOCK_CODES.has(e.code);
}

type AccessBlockedHandler = (e: ApiError) => void;
let onAccessBlocked: AccessBlockedHandler | null = null;

/** 利用不可（IP 制限・端末証明書）受信時の処理を登録（AccessGate が登録する） */
export function setAccessBlockedHandler(handler: AccessBlockedHandler | null): void {
  onAccessBlocked = handler;
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** 401 受信時の処理を登録（AuthProvider が登録する） */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;
export type QueryParams = Record<string, QueryValue>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
    else sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface RequestOptions {
  query?: QueryParams;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** true の場合 401 でもログイン遷移しない（/auth/me など） */
  skipUnauthorizedRedirect?: boolean;
}

export function apiUrl(path: string, query?: QueryParams): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${p}${buildQuery(query)}`;
}

/** 低レベル: Response を返す（ダウンロード等で使用）。エラー時は ApiError を throw */
export async function rawRequest(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
    ...opts.headers,
  };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path, opts.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK_ERROR', 'サーバーに接続できません。ネットワーク接続を確認してください。');
  }

  if (!res.ok) {
    const err = await toApiError(res);
    if (res.status === 401 && !opts.skipUnauthorizedRedirect) onUnauthorized?.();
    if (isAccessBlockedError(err)) onAccessBlocked?.(err);
    throw err;
  }
  return res;
}

async function toApiError(res: Response): Promise<ApiError> {
  let parsed: Partial<ApiErrorBody> | null = null;
  try {
    const text = await res.text();
    parsed = text ? (JSON.parse(text) as Partial<ApiErrorBody>) : null;
  } catch {
    parsed = null;
  }
  const e = parsed?.error;
  const fallback = DEFAULT_MESSAGES[res.status] ?? DEFAULT_MESSAGES[500] ?? 'エラーが発生しました。';
  return new ApiError(res.status, e?.code ?? `HTTP_${res.status}`, e?.message || fallback, e?.details);
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await rawRequest(method, path, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** よく使う呼び出しの短縮形 */
export const api = {
  get: <T>(path: string, query?: QueryParams, opts?: Omit<RequestOptions, 'query' | 'body'>) =>
    request<T>('GET', path, { ...opts, query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T = void>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};
